// Contact type for frontend
export interface Contact {
  is_valid: boolean;
  title: string | null;
  first_name: string | null;
  last_name: string | null;
  full_name: string | null;
  email: string | null;
  mobile_phone: string | null;
  home_phone: string | null;
  work_phone: string | null;
  street_address: string | null;
  suburb: string | null;
  postcode: string | null;
  state: string | null;
  coordinates: string | null;
  source_row_number: number | null;
  notes: string | null;
  contact_id: string;
  extraction_timestamp: string;
  source_boards: string[];
  is_deleted?: boolean;
  deleted_at?: string | null;
  is_unsubscribed?: boolean;
  is_bounced?: boolean;
  bounced_at?: string | null;
  profile_photo?: string | null; // Base64 encoded image (100x100)
}

// Email types
export interface EmailRecipient {
  contact_id: string;
  email: string;
  name: string;
  first_name?: string | null;
  last_name?: string | null;
}

export interface EmailHistory {
  email_id: string;
  subject: string;
  html_body: string;
  sent_at: string;
  sent_by_email: string;
  recipient_count: number;
  recipients: EmailRecipient[];
  successful_recipients?: EmailRecipient[];
  failed_recipients?: Array<EmailRecipient & { error: string }>;
}

export interface EmailStats {
  totalOpens: number;
  uniqueOpens: number;
  opens: Array<{
    email_id: string;
    contact_id: string;
    contact_email: string;
    opened_at: string;
  }>;
}

// Email Template types
export interface EmailTemplate {
  template_id: string;
  user_id: string;
  name: string;
  description: string;
  type: "default" | "custom";
  html_content: string;
  markers: string[];
  created_at: string;
  updated_at: string;
}

export interface TemplateProcessingResult {
  processedHtml: string;
  insertionPoints: Array<{
    type: "content" | "subject" | "signature";
    marker: string;
    description: string;
    required: boolean;
  }>;
  compatibilityFixes: Array<{
    original: string;
    fixed: string;
    reason: string;
  }>;
  warnings: string[];
}

/**
 * Get avatar color based on name
 */
export function getAvatarColor(name: string | null): string {
  if (!name) return "from-navy-light to-navy-lighter";
  const colors = [
    "from-rose-400 to-pink-500",
    "from-amber-400 to-orange-500",
    "from-emerald-400 to-teal-500",
    "from-sky-400 to-blue-500",
    "from-navy-light to-rose-accent",
    "from-rose-accent to-rose-bright",
    "from-lime-400 to-green-500",
    "from-cyan-400 to-teal-500",
  ];
  const hash = name.split("").reduce((acc, char) => acc + char.charCodeAt(0), 0);
  return colors[hash % colors.length];
}

/**
 * Validate email address format
 */
export function isValidEmail(email: string): boolean {
  const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
  return emailRegex.test(email);
}
